import { useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { navLinks, headerLogoUrl, createTranslator } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export const Header = ({ language }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const resolveHref = (href) => (isHome ? href : `/${href}`);
  const contactHref = isHome ? "#contact" : "/#contact";
  const t = createTranslator(language);

  return (
    <header
      className="fixed top-0 w-full z-50 bg-white/90 backdrop-blur-md border-b border-gray-100"
      data-testid="site-header"
    >
      <nav
        className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 flex items-center justify-between py-3"
        data-testid="primary-navigation"
      >
        <div className="flex items-center gap-3 flex-shrink-0" data-testid="brand-block">
          <a href="/" data-testid="brand-home-link">
            <img
              loading="lazy"
              src={headerLogoUrl}
              alt="Infraglobal Engineering logo"
              className="h-[60px] md:h-[75px] lg:h-[95px] w-auto object-contain"
              data-testid="brand-logo"
            />
          </a>
        </div>
        <div className="hidden lg:flex items-center gap-4" data-testid="nav-links-desktop">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={resolveHref(link.href)}
              className="text-sm font-semibold uppercase tracking-wider text-slate-600 hover:text-[#003366] transition-colors"
              data-testid={link.testid}
            >
              {t(`nav.${link.key}`, link.label)}
            </a>
          ))}
          <Link
            to="/admin-login"
            className="text-sm font-semibold uppercase tracking-wider text-slate-600 hover:text-[#003366] transition-colors"
            data-testid="nav-admin-link"
          >
            {t("header.admin", "ADMIN")}
          </Link>
        </div>
        <div className="flex items-center gap-3">
          <Button
            className="bg-transparent border border-[#003366]/30 text-[#003366] hover:bg-[#003366]/5 rounded-none px-5 py-2 text-xs font-bold uppercase tracking-widest"
            asChild
            data-testid="nav-contact-cta"
          >
            <a href={contactHref} data-testid="nav-contact-cta-link">
              {t("header.engage", "Engage")}
            </a>
          </Button>
          <button
            className="lg:hidden border border-slate-200 px-3 py-2 text-xs uppercase tracking-widest"
            onClick={() => setMenuOpen((prev) => !prev)}
            data-testid="nav-mobile-toggle"
          >
            {t("header.menu", "Menu")}
          </button>
        </div>
      </nav>
      {menuOpen && (
        <div
          className="lg:hidden bg-white border-t border-slate-100 px-6 pb-6"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-3 pt-4">
            {navLinks.map((link) => (
              <a
                key={`${link.href}-mobile`}
                href={resolveHref(link.href)}
                className="text-sm font-semibold uppercase tracking-wider text-slate-600"
                onClick={() => setMenuOpen(false)}
                data-testid={`${link.testid}-mobile`}
              >
                {t(`nav.${link.key}`, link.label)}
              </a>
            ))}
            <Link
              to="/admin-login"
              className="text-sm font-semibold uppercase tracking-wider text-slate-600"
              data-testid="nav-admin-link-mobile"
              onClick={() => setMenuOpen(false)}
            >
              {t("header.admin", "ADMIN")}
            </Link>
            <Button
              className="bg-transparent border border-[#003366]/30 text-[#003366] hover:bg-[#003366]/5 rounded-none px-5 py-2 text-xs font-bold uppercase tracking-widest"
              asChild
              data-testid="nav-contact-cta-mobile"
            >
              <a href={contactHref} data-testid="nav-contact-cta-link-mobile">
                {t("header.engage", "Engage")}
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
